import React, { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X, MoreHorizontal, ChevronDown } from "lucide-react";

const KEY = "rosterly_pwa_dismissed_at";
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isMobile = () => window.matchMedia("(max-width: 1023px)").matches && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
const snoozed = () => { try { return Date.now() - Number(localStorage.getItem(KEY) || 0) < SNOOZE_MS; } catch { return false; } };

const IOS_STEPS = [
    <>Press <MoreHorizontal className="h-4 w-4" /> in Safari</>,
    <>Tap <Share className="h-4 w-4 text-[#3B82F6]" /> <b>Share</b> in the toolbar</>,
    <>Tap <ChevronDown className="h-4 w-4" /> <b>View More</b></>,
    <>Choose <PlusSquare className="h-4 w-4" /> <b>Add to Home Screen</b></>,
    <>Tap <b>Add</b></>,
];

// Mobile banner — native install on Android, step guide on iOS Safari.
// "Add Browser App" (My Account): native install where the browser offers it
// (Chrome / Edge / Opera / Brave…), otherwise the exact steps for that browser.
export const addBrowserApp = async (toast) => {
    if (isStandalone()) { toast.success("You're already using the Rosterly app"); return; }
    const bip = window.__rosterlyBip;
    if (bip) {
        bip.prompt();
        const { outcome } = await bip.userChoice.catch(() => ({}));
        window.__rosterlyBip = null;
        if (outcome === "accepted") toast.success("Rosterly app added");
        return;
    }
    const ua = navigator.userAgent;
    const steps = /OPR\/|Opera/.test(ua) ? "Opera: click the install icon at the right of the address bar, or Menu → Install Rosterly"
        : /Edg\//.test(ua) ? "Edge: Menu (…) → Apps → Install Rosterly"
        : /Firefox\//.test(ua) ? "Firefox: Menu → Add to taskbar (Windows), or open Rosterly in Chrome, Edge or Opera to install it"
        : /Safari\//.test(ua) && !/Chrome\//.test(ua) ? (isIos() ? "Safari: Share → Add to Home Screen" : "Safari: File → Add to Dock")
        : "Chrome: click the install icon at the right of the address bar, or Menu (⋮) → Cast, save and share → Install Rosterly";
    toast(`Your browser didn't offer the install popup here (usually because Rosterly is already installed in this browser, or the popup was dismissed before). ${steps}`, { duration: 10000 });
};

export default function InstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [mode, setMode] = useState(null); // "android" | "ios" | null
    const [preview, setPreview] = useState(false);

    useEffect(() => {
        if (isStandalone()) return undefined;
        const onBip = (e) => { e.preventDefault(); window.__rosterlyBip = e; setDeferred(e); if (!snoozed()) setMode("android"); };
        const onInstalled = () => { setMode(null); setDeferred(null); };
        // index.html captures the offer early; pick it up if it already fired.
        const onEarly = () => { if (window.__rosterlyBip) { setDeferred(window.__rosterlyBip); if (!snoozed()) setMode("android"); } };
        onEarly();
        window.addEventListener("rosterly:bip", onEarly);
        window.addEventListener("beforeinstallprompt", onBip);
        window.addEventListener("appinstalled", onInstalled);
        if (isIos() && isMobile() && !snoozed()) setMode("ios");
        return () => { window.removeEventListener("rosterly:bip", onEarly); window.removeEventListener("beforeinstallprompt", onBip); window.removeEventListener("appinstalled", onInstalled); };
    }, []);

    useEffect(() => {
        const onPreview = (e) => { setPreview(true); setMode(e.detail === "android" ? "android" : "ios"); };
        window.addEventListener("rosterly:pwa-preview", onPreview);
        return () => window.removeEventListener("rosterly:pwa-preview", onPreview);
    }, []);

    if (!mode) return null;
    const dismiss = () => {
        if (!preview) { try { localStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ } }
        setMode(null); setPreview(false);
    };
    const install = async () => {
        if (!deferred) { dismiss(); return; }
        deferred.prompt();
        const { outcome } = await deferred.userChoice.catch(() => ({}));
        setDeferred(null);
        if (outcome === "accepted") setMode(null); else dismiss();
    };

    return (
        <div className="fixed left-3 right-3 lg:left-auto lg:right-6 lg:w-[380px] bottom-[calc(60px+env(safe-area-inset-bottom))] lg:bottom-6 z-[100001] rounded-2xl border border-white/15 bg-[#0B0C13]/95 backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.6)] p-4" data-testid="pwa-install-banner" role="dialog" aria-label="Install Rosterly">
            <button type="button" onClick={dismiss} aria-label="Dismiss" data-testid="pwa-install-dismiss" className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"><X className="h-4 w-4" /></button>
            <div className="flex items-center gap-3 pr-6">
                <img src="/logo192.png" alt="" className="h-12 w-12 rounded-xl shrink-0" />
                <div className="min-w-0">
                    <div className="text-sm font-black text-white">Add Rosterly to your Home Screen</div>
                    <div className="text-[11px] text-white/55">Full-screen, faster launch, live game alerts.</div>
                </div>
            </div>
            {mode === "android" && (
                <button type="button" onClick={install} data-testid="pwa-install-btn" className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#9D4CDD] hover:bg-[#B366F0] text-white text-xs font-black uppercase tracking-wider transition-colors">
                    <Download className="h-4 w-4" /> Install app
                </button>
            )}
            {mode === "ios" && (
                <ol className="mt-3 space-y-1.5 text-xs text-white/80" data-testid="pwa-ios-steps">
                    {IOS_STEPS.map((txt, i) => (
                        <li key={i} className="flex items-center gap-2 px-1.5 py-0.5" data-testid={`pwa-ios-step-${i + 1}`}>
                            <span className="h-5 w-5 rounded-full text-[10px] font-black flex items-center justify-center bg-white/10 shrink-0">{i + 1}</span> {txt}
                        </li>
                    ))}
                </ol>
            )}
            {preview && <div className="mt-2 text-[10px] text-[#F5B301] font-bold uppercase tracking-wider" data-testid="pwa-preview-tag">Preview</div>}
        </div>
    );
}
